(function () {
  'use strict';

  const WARNING =
    'For your security, do not enter card numbers, security codes, or payment credentials here. Payments are entered only in secure checkout.';
  const ERROR =
    'Remove payment card details before continuing. Enter payment information only in secure checkout.';
  const CANDIDATE_SELECTOR = 'textarea,input[type="text"],input:not([type])';
  const INSTALLED_DOCUMENTS = new WeakMap();
  let generatedFieldId = 0;

  function luhn(value) {
    let total = 0;
    let doubleDigit = false;
    for (let index = value.length - 1; index >= 0; index -= 1) {
      let digit = Number(value[index]);
      if (doubleDigit) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      total += digit;
      doubleDigit = !doubleDigit;
    }
    return total % 10 === 0;
  }

  function hasPanLikeValue(value) {
    // Match each complete digit run, including every supported separator,
    // before applying length or Luhn. Consuming the whole run prevents the
    // matcher from restarting after the first digit of an overlength value and
    // accepting a valid 19-digit suffix as though it were a separate PAN.
    const candidates = String(value || '').match(/\d(?:[\s.\-]*\d)*/g) || [];
    return candidates.some((candidate) => {
      const digits = candidate.replace(/\D/g, '');
      return digits.length >= 13 && digits.length <= 19 && luhn(digits);
    });
  }

  function hasSecurityCodeContext(value) {
    // Bounded label grammar:
    //   acronym label: CVV/CVC/CAV/CVN (optionally 2), CID, or CSC,
    //     optionally followed by code/value/number;
    //   descriptive label: card security/verification code/value/number;
    //   optional connector: is/equals, optional punctuation, then 3-4 digits.
    return /\b(?:(?:(?:cvv|cvc|cav|cvn)(?:\s*2)?|cid|csc)(?:\s+(?:code|value|number))?|(?:card\s+)?(?:security|verification)\s+(?:code|value|number))\b\s*(?:(?:is|equals)\b\s*)?(?:[:#=\-]\s*)?\d{3,4}\b/i.test(
      String(value || ''),
    );
  }

  function containsPaymentData(value) {
    return hasPanLikeValue(value) || hasSecurityCodeContext(value);
  }

  function normalizedTag(field) {
    return String(field && field.tagName ? field.tagName : '').toLowerCase();
  }

  function fieldType(field) {
    const explicit = field && typeof field.getAttribute === 'function' ? field.getAttribute('type') : '';
    return String(explicit || field.type || 'text').toLowerCase();
  }

  function fieldName(field) {
    const explicit = field && typeof field.getAttribute === 'function' ? field.getAttribute('name') : '';
    return String(explicit || field.name || '');
  }

  function contactFormFor(field) {
    if (!field || typeof field.closest !== 'function') return null;
    const form = field.closest('form');
    if (!form || typeof form.getAttribute !== 'function') return null;
    return String(form.getAttribute('action') || '').includes('/contact') ? form : null;
  }

  function isGuardedField(field) {
    const tag = normalizedTag(field);
    const type = fieldType(field);
    const name = fieldName(field);
    const isTextControl = tag === 'textarea' || (tag === 'input' && type === 'text');
    if (!isTextControl) return false;
    if (contactFormFor(field)) return true;
    if (name.startsWith('properties[')) return true;
    return tag === 'textarea' && name === 'note';
  }

  function warningId(field) {
    if (!field.id) {
      generatedFieldId += 1;
      field.id = `lt-payment-data-field-${generatedFieldId}`;
    }
    return `${field.id}-payment-warning`;
  }

  function enhanceField(field, doc) {
    if (!isGuardedField(field)) return false;
    if (field.dataset.ltPaymentDataGuard === 'true') return true;

    field.dataset.ltPaymentDataGuard = 'true';
    const id = warningId(field);
    const warning = doc.createElement('p');
    warning.id = id;
    warning.className = 'lt-payment-data-warning';
    warning.dataset.ltPaymentDataWarning = 'true';
    warning.textContent = WARNING;
    field.insertAdjacentElement('afterend', warning);

    const describedBy = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    describedBy.add(id);
    field.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    return true;
  }

  function fieldsWithin(scope) {
    const fields = [];
    if (isGuardedField(scope)) fields.push(scope);
    if (scope && typeof scope.querySelectorAll === 'function') {
      for (const field of scope.querySelectorAll(CANDIDATE_SELECTOR)) {
        if (isGuardedField(field)) fields.push(field);
      }
    }
    return Array.from(new Set(fields));
  }

  function enhanceWithin(scope, doc) {
    const fields = fieldsWithin(scope);
    fields.forEach((field) => enhanceField(field, doc));
    return fields;
  }

  function isForm(target) {
    return normalizedTag(target) === 'form';
  }

  function install(doc) {
    if (!doc || typeof doc.querySelectorAll !== 'function' || typeof doc.addEventListener !== 'function') {
      throw new Error('PAYMENT DATA GUARD BLOCKED: a document with query and event support is required.');
    }
    if (INSTALLED_DOCUMENTS.has(doc)) return INSTALLED_DOCUMENTS.get(doc);

    enhanceWithin(doc, doc);

    const onSubmit = (event) => {
      if (!isForm(event.target)) return;
      const guarded = enhanceWithin(event.target, doc);
      guarded.forEach((field) => field.setCustomValidity(''));
      const unsafe = guarded.find((field) => containsPaymentData(field.value));
      if (!unsafe) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      unsafe.setCustomValidity(ERROR);
      unsafe.reportValidity();
      unsafe.focus();
    };

    const onFieldInteraction = (event) => {
      const field = event.target;
      if (!isGuardedField(field)) return;
      enhanceField(field, doc);
      if (event.type === 'input') field.setCustomValidity('');
    };

    doc.addEventListener('submit', onSubmit, true);
    doc.addEventListener('input', onFieldInteraction, true);
    doc.addEventListener('focusin', onFieldInteraction, true);

    const MutationObserverClass = doc.defaultView && doc.defaultView.MutationObserver;
    let observer = null;
    if (MutationObserverClass && doc.documentElement) {
      observer = new MutationObserverClass((records) => {
        records.forEach((record) => {
          Array.from(record.addedNodes || []).forEach((node) => enhanceWithin(node, doc));
        });
      });
      observer.observe(doc.documentElement, { childList: true, subtree: true });
    }

    const receipt = { observer, onSubmit, onFieldInteraction };
    INSTALLED_DOCUMENTS.set(doc, receipt);
    return receipt;
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => install(document), { once: true });
    } else {
      install(document);
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      CANDIDATE_SELECTOR,
      ERROR,
      WARNING,
      containsPaymentData,
      enhanceWithin,
      hasPanLikeValue,
      hasSecurityCodeContext,
      install,
      isGuardedField,
      luhn,
    };
  }
})();
